"use client";

import { useRef, useState, useEffect, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Subtle floating geometry
function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);
  const mesh3Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.02;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.1;
      mesh1Ref.current.rotation.z = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.3) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.08;
      mesh2Ref.current.position.y = Math.sin(t * 0.4 + 1) * 0.2;
    }
    if (mesh3Ref.current) {
      mesh3Ref.current.rotation.x = t * 0.06;
      mesh3Ref.current.rotation.y = t * 0.04;
      mesh3Ref.current.position.y = Math.sin(t * 0.35 + 2) * 0.25;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[2, 0, 0]}>
        <octahedronGeometry args={[2.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.4} transparent />
      </mesh>
      <mesh ref={mesh2Ref} position={[-2, 1, -1]}>
        <icosahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>
      <mesh ref={mesh3Ref} position={[0, -1.5, -2]}>
        <dodecahedronGeometry args={[1.2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>
    </group>
  );
}

// Subtle grid of dots
function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.01;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

// Typewriter hook
function useTypewriter(text: string, speed: number = 50, delay: number = 0) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayedText, isComplete };
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    website: "",
    message: "",
    company: "", // Honeypot field — hidden from real users
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [formLoadedAt] = useState(() => Date.now());

  const { displayedText: heroText, isComplete: heroComplete } = useTypewriter(
    "Let's build something great together",
    40,
    300
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, timestamp: formLoadedAt }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setSubmitStatus("success");
      setFormData({ name: "", email: "", website: "", message: "", company: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus("idle"), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden flex flex-col">
      {/* Three.js Canvas Background */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        <Navigation />

        <main id="main-content">

        {/* Hero */}
        <section className="pt-32 pb-8 md:pb-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl">
              <div className="min-h-[2.4em]">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-6 lg:whitespace-nowrap">
                  {heroText.includes("great together") ? (
                    <>
                      {heroText.slice(0, heroText.indexOf("great together"))}
                      <span className="text-[#ff6900]">{heroText.slice(heroText.indexOf("great together"))}</span>
                    </>
                  ) : (
                    heroText
                  )}
                  <span
                    className={`inline-block w-[3px] h-[0.9em] bg-[#ff6900] ml-1 align-middle ${heroComplete ? 'opacity-0' : 'animate-pulse'}`}
                  />
                </h1>
              </div>
              <p
                className="text-xl text-[#1a1a1a]/70 leading-relaxed transition-all duration-500"
                style={{
                  opacity: heroComplete ? 1 : 0,
                  transform: heroComplete ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                We partner with teams to design, build, and scale digital platforms. Tell us a bit about what you&apos;re working on.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section
          className="pt-8 md:pt-12 pb-16 md:pb-24 px-6 transition-all duration-700"
          style={{
            opacity: heroComplete ? 1 : 0,
            transform: heroComplete ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#1a1a1a]/5">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot — invisible to users, bots auto-fill it */}
                    <div className="absolute opacity-0 -z-10" style={{ top: '-9999px', left: '-9999px' }} aria-hidden="true">
                      <label htmlFor="company">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-[#1a1a1a]/70 mb-2">
                          Your Name <span className="text-[#ff6900]">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-[#fafafa] border border-[#1a1a1a]/10 focus:border-[#ff6900]/50 focus:ring-2 focus:ring-[#ff6900]/20 outline-none transition-all"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-[#1a1a1a]/70 mb-2">
                          Email Address <span className="text-[#ff6900]">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-[#fafafa] border border-[#1a1a1a]/10 focus:border-[#ff6900]/50 focus:ring-2 focus:ring-[#ff6900]/20 outline-none transition-all"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="website" className="block text-sm font-medium text-[#1a1a1a]/70 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-[#fafafa] border border-[#1a1a1a]/10 focus:border-[#ff6900]/50 focus:ring-2 focus:ring-[#ff6900]/20 outline-none transition-all"
                        placeholder="https://yourcompany.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#1a1a1a]/70 mb-1">
                        How can we help? <span className="text-[#ff6900]">*</span>
                      </label>
                      <p className="text-xs text-[#1a1a1a]/70 mb-2">(Goals, challenges, or systems you&apos;re trying to improve)</p>
                      <textarea
                        id="message"
                        name="message"
                        required
                        minLength={15}
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-[#fafafa] border border-[#1a1a1a]/10 focus:border-[#ff6900]/50 focus:ring-2 focus:ring-[#ff6900]/20 outline-none transition-all resize-none"
                        placeholder="Tell us about your project..."
                      />
                    </div>

                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            Start the conversation
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </>
                        )}
                      </button>

                      {submitStatus === "success" && (
                        <p className="mt-4 text-green-600 font-medium">
                          Thanks for reaching out! We&apos;ll be in touch soon.
                        </p>
                      )}

                      {submitStatus === "error" && (
                        <p className="mt-4 text-red-600 font-medium">
                          Something went wrong. Please try again or email us directly.
                        </p>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              {/* Contact Info Sidebar */}
              <div className="space-y-8">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-[#1a1a1a]/5">
                  <h2 className="text-lg font-semibold mb-4">Get in touch</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-[#1a1a1a]/70 mb-1">Email</p>
                      <a
                        href="mailto:sales@mapletonhill.com"
                        className="text-[#ff6900] hover:underline font-medium"
                      >
                        sales@mapletonhill.com
                      </a>
                    </div>
                    <div>
                      <p className="text-sm text-[#1a1a1a]/70 mb-1">Office</p>
                      <p className="text-[#1a1a1a]/70">
                        1942 Broadway Street, Suite 501<br />
                        Boulder, CO 80302
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#ff6900]/10 to-[#ff6900]/5 rounded-2xl p-8 border border-[#1a1a1a]/5">
                  <h2 className="text-lg font-semibold mb-4">Your partner in innovation</h2>
                  <p className="text-[#1a1a1a]/70 leading-relaxed">
                    We&apos;re a senior engineering partner for teams building complex digital platforms. From strategy through delivery, we help organizations move faster with confidence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        </main>

        <div
          className="transition-opacity duration-700 mt-auto"
          style={{ opacity: heroComplete ? 1 : 0 }}
        >
          <Footer />
        </div>
      </div>
    </div>
  );
}
